import type { Migration, MigrationDevice } from '../api/migrations';

// The six-step campaign journey the R-FD-051 mockups draw (FD-490).
//
// Derived at READ TIME from facts already on `GET /v1/migrations/{id}`. Nothing
// here is stored, and nothing here is new on the wire: the epic's first finding
// was that the lifecycle is fully knowable and nowhere expressed —
// `lib/campaignStatus.ts` derives a BADGE from `bprtStatus` and stops.
//
// NOTHING HERE READS `Migrations.Status`. It is written `"active"` at creation
// and no writer in the codebase ever updates it (FD-269), so a journey derived
// from it would sit on step one forever for every campaign in the deployment.
// The lifecycle that actually moves is `bprtStatus` — the wave credential —
// plus the package state and the devices' own rows. `campaignStatus.ts` and
// `groupRollup.ts` both open with this sentence; it is repeated rather than
// cross-referenced because the next person to add a derivation will open one
// file, not three.
//
// THE JUDGEMENT LIVES HERE RATHER THAN IN A COMPONENT for the reason
// `devicePhase.ts` gives: two readers of the same rule will eventually disagree,
// and a rule inside a component cannot be asserted against a fixture.

export type CampaignStepId =
  | 'add-devices'
  | 'map-identities'
  | 'activate-wave'
  | 'build-package'
  | 'migrate'
  | 'complete';

/**
 * Three states, and they are EXHAUSTIVE AND MUTUALLY EXCLUSIVE — exactly one
 * step is `current`, every earlier step is `done`, every later one is `pending`.
 *
 * That is the same guarantee `groupRollup`'s tab counts carry, and it is
 * asserted directly (`campaignLifecycle.test.ts`) rather than hoped for: a
 * stepper showing two current steps, or a gap, reads as a broken control rather
 * than as a campaign in an unusual state.
 */
export type LifecycleStepState = 'done' | 'current' | 'pending';

export interface LifecycleStep {
  id: CampaignStepId;
  label: string;
  /** The one-line caption under the label, as the mockups draw it. */
  caption: string;
  state: LifecycleStepState;
  /**
   * A short factual line about THIS campaign — a count, or a live blocker.
   * Absent when there is nothing true to say (the `devicePhase.ts` rule: never
   * invent a value, and never claim a step passed on the evidence that a later
   * one started).
   */
  detail?: string;
}

/**
 * What the operator should do next, for the top-bar CTA (FD-491).
 *
 * `to` is an absolute console route so the lib stays pure — it takes the
 * migration id in and hands a path back, rather than reaching for a router.
 */
export interface CampaignNextAction {
  stepId: CampaignStepId;
  label: string;
  to: string;
  /** One line, in the operator's terms, for the caption beside the button. */
  reason: string;
}

export interface CampaignLifecycleView {
  steps: LifecycleStep[];
  /** Index into `steps` of the one `current` step. Always 0–5, never -1. */
  currentIndex: number;
  /**
   * `null` ON A FINISHED CAMPAIGN, and deliberately so.
   *
   * A completed wave has no next action, and a top bar that invents one — "View
   * summary", on the page the operator is already looking at — is a button that
   * teaches them the console makes things up. Typing it nullable is what forces
   * FD-491 to render nothing rather than something plausible.
   */
  nextAction: CampaignNextAction | null;
}

const LAST_INDEX = 5;

const STEP_META: { id: CampaignStepId; label: string; caption: string }[] = [
  { id: 'add-devices', label: 'Add devices', caption: 'Assign devices to this campaign' },
  { id: 'map-identities', label: 'Map identities', caption: 'Reconcile source to destination' },
  // NOT "Lock campaign and mint credentials", which is what the mockup draws.
  // The handoff's central claim — that activation locks campaign membership — is
  // REFUSED by R-FD-051 D1 and by three ratified decisions: `add-devices` has no
  // `BprtStatus` guard by design, and its active-wave branch exists to top up the
  // licence reservation (FD-ADR-041 §5); FD-ADR-035 §3 makes remove+add a failed
  // device's only retry path; and FD-ADR-036 narrowed the identity freeze to
  // additive-only precisely so a late mapping can release a held device WHILE THE
  // WAVE RUNS. A caption is a small place for a false claim to live, which is
  // exactly why it would survive review.
  { id: 'activate-wave', label: 'Activate wave', caption: 'Mint the wave credential' },
  { id: 'build-package', label: 'Build package', caption: 'Create the provisioning package' },
  { id: 'migrate', label: 'Migrate', caption: 'Devices migrate to destination' },
  { id: 'complete', label: 'Complete', caption: 'Revoke the credential and close the wave' },
];

/**
 * `stored` and `delivered` are both servable to a device — `api/ppkg.ts:40`,
 * which is the authority for this predicate.
 *
 * RESTATED HERE RATHER THAN IMPORTED, and the reason is the module boundary
 * rather than taste. `isPpkgServable` is a *value*, and `api/ppkg.ts`
 * value-imports `api/client.ts`, which value-imports `@azure/msal-browser` and
 * the process-wide MSAL instance. Importing it would drag the auth stack into a
 * pure derivation and into its unit test — the thing every other module in
 * `lib/` avoids by importing only `type`s, and the thing FD-490's acceptance
 * criteria spell out.
 *
 * Restating a predicate invites drift, so the drift is TESTED rather than
 * trusted: `campaignLifecycle.test.ts` imports the real `isPpkgServable` and
 * requires the two to agree across the whole `PpkgStatus` union plus `null` and
 * `undefined`. A comment saying "keep these in sync" does not fail; that does.
 */
function ppkgServable(status: Migration['ppkgStatus']): boolean {
  return status === 'stored' || status === 'delivered';
}

const TERMINAL_STATUSES = new Set(['completed', 'aborted', 'salvaged', 'faulted']);

function normalise(value: string | null | undefined): string {
  return (value ?? '').trim().toLowerCase();
}

/**
 * Has this device started migrating, or finished?
 *
 * READS BOTH `status` AND `terminalOutcome`. FD-345 made them separate server
 * columns precisely so a concurrent merge that regresses `Status` under a
 * surviving `TerminalOutcome` stays detectable — that is what FD-362's
 * heal-on-read keys off, after two live rows sat at `status: in-progress` under
 * `terminalOutcome: completed`, invisible to wave evaluation. A derivation that
 * read only one of them would put such a campaign back a step.
 */
function hasDeviceStarted(device: Pick<MigrationDevice, 'status' | 'terminalOutcome'>): boolean {
  const status = normalise(device.status);
  const outcome = normalise(device.terminalOutcome);
  return status === 'in-progress' || TERMINAL_STATUSES.has(status) || TERMINAL_STATUSES.has(outcome);
}

type LifecycleMigration = Pick<
  Migration,
  'migrationId' | 'deviceCount' | 'bprtStatus' | 'ppkgStatus'
>;

type LifecycleDevice = Pick<MigrationDevice, 'status' | 'terminalOutcome' | 'identityStatus'>;

export function deriveCampaignLifecycle(
  migration: LifecycleMigration,
  devices: LifecycleDevice[] = [],
): CampaignLifecycleView {
  const bprt = normalise(migration.bprtStatus);
  const deviceCount = migration.deviceCount ?? 0;
  const notReady = devices.filter((d) => d.identityStatus === 'not-ready').length;
  const started = devices.filter(hasDeviceStarted).length;

  // Step 3 treats `notActivated` as NOT activated, because that is what the word
  // says. `bprtStatus` is `null` on a wave that has never been activated and the
  // literal `'notActivated'` on rows that carry the vocabulary spelled out
  // (`deriveCampaignStatus` handles both, and `WaveBprtStatusView` documents the
  // spelling). A bare non-empty test would advance the stepper two steps on the
  // second of those — the FD-269 class of defect, a field reporting a state it
  // cannot know.
  const waveActivated = bprt !== '' && bprt !== 'notactivated';

  // STEP 2 IS ANSWERED, NOT MERELY UNCONTRADICTED (FD-500).
  //
  // `notReady === 0` was the original predicate and it is VACUOUSLY TRUE before
  // any manifest arrives: a device with no `PrimaryUserSid` projects `'n/a'`,
  // not `'not-ready'` (`manager/get-migration/run.ps1:368`, the FD-246
  // notResolved-vs-none discipline applied server-side). So a campaign one
  // minute old — one device assigned, nothing checked in — rendered step 2
  // DONE with its own caption reading "1 awaiting a manifest" directly beneath
  // the tick, and pointed the operator at Activate wave. Reading "nobody is
  // not-ready" as "everybody is mapped" is the FD-269 class of defect: a field
  // reporting a state it cannot know.
  //
  // The step is done only when every device is `'ready'` — a SID matched to a
  // mapping whose `destUserId` parses as a GUID, which is the same predicate
  // FD-ADR-036's delivery gate holds a device at `409 IDENTITY_NOT_READY` for.
  // `'n/a'` stays ORDINARY in every other respect (never a warning colour, per
  // the `identityStatus` contract); ordinary is not the same as answered.
  //
  // THE FALLBACK IS DELIBERATE. A Manager predating FD-357 omits the field
  // entirely, and `every(=== 'ready')` over a fleet of nulls would strand such a
  // deployment on step 2 forever with no way to activate. When NOTHING is
  // reported the old predicate stands and `identityDetail` says so out loud
  // ("Identity readiness not reported") — degrade to the previous behaviour,
  // never to a block.
  const identityReported = devices.some(
    (d) => d.identityStatus !== undefined && d.identityStatus !== null,
  );
  const identitiesAnswered = identityReported
    ? devices.length > 0 && devices.every((d) => d.identityStatus === 'ready')
    : notReady === 0;

  const satisfied: boolean[] = [
    deviceCount > 0,
    deviceCount > 0 && identitiesAnswered,
    waveActivated,
    ppkgServable(migration.ppkgStatus),
    started > 0,
    bprt === 'revoked',
  ];

  // MONOTONE BY FURTHEST EVIDENCE, not by first-unsatisfied.
  //
  // A later fact is evidence the earlier steps were walked: a campaign holding a
  // live wave credential passed through "add devices" whatever its device count
  // reads today — FD-ADR-035 §3 makes remove+add a real mid-wave operation, so a
  // wave can legitimately sit at zero assigned devices. Taking the first
  // unsatisfied step instead would send a completed campaign's CTA back to "Add
  // devices", and would let the stepper show step 5 done above a pending step 1,
  // which reads as a broken control rather than as a campaign.
  //
  // THE COST, STATED BECAUSE IT IS REAL: a device held at
  // `409 IDENTITY_NOT_READY` mid-wave (FD-ADR-036 — an ordinary state, and the
  // late mapping that releases it is allowed precisely while the wave runs)
  // leaves step 2 rendered `done`. The spine is a journey, not a health readout,
  // so the live blocker rides the step's `detail` and is surfaced properly by
  // `campaignPreflight`'s identity check and by the Devices tab's own filter.
  let furthest = -1;
  for (let i = 0; i <= LAST_INDEX; i += 1) {
    if (satisfied[i]) furthest = i;
  }
  const currentIndex = Math.min(furthest + 1, LAST_INDEX);

  const details: (string | undefined)[] = [
    deviceCount > 0 ? `${plural(deviceCount, 'device', 'devices')} assigned` : undefined,
    identityDetail(devices, notReady),
    waveActivated ? `Wave credential ${bprt}` : undefined,
    migration.ppkgStatus ? `Package ${migration.ppkgStatus}` : undefined,
    started > 0 ? `${started} of ${devices.length} devices under way` : undefined,
    bprt === 'revoked' ? 'Wave credential revoked' : undefined,
  ];

  const steps: LifecycleStep[] = STEP_META.map((meta, index) => {
    const step: LifecycleStep = {
      ...meta,
      state: index < currentIndex ? 'done' : index === currentIndex ? 'current' : 'pending',
    };
    const detail = details[index];
    if (detail) step.detail = detail;
    return step;
  });

  return {
    steps,
    currentIndex,
    nextAction: buildNextAction(migration.migrationId, currentIndex, { notReady, satisfied }),
  };
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/**
 * The identity line, which must survive three DIFFERENT absences without
 * claiming the same thing about any two of them (the FD-246 discipline):
 *
 *   * no devices loaded at all   — nothing to say, so nothing is said
 *   * a Manager predating FD-357 — `identityStatus` is absent, so the console
 *                                  was never told; "all mapped" would be
 *                                  reporting a state it cannot know
 *   * devices at `n/a`           — pre-manifest, an ORDINARY state and never a
 *                                  warning, which the `identityStatus` contract
 *                                  says in terms
 */
function identityDetail(devices: LifecycleDevice[], notReady: number): string | undefined {
  if (devices.length === 0) return undefined;
  if (devices.every((d) => d.identityStatus === undefined || d.identityStatus === null)) {
    return 'Identity readiness not reported';
  }
  if (notReady > 0) return `${plural(notReady, 'device', 'devices')} waiting on a mapping`;
  const pending = devices.filter((d) => d.identityStatus === 'n/a').length;
  if (pending > 0) return `${pending} awaiting a manifest`;
  return 'All devices mapped';
}

function buildNextAction(
  migrationId: string,
  currentIndex: number,
  facts: { notReady: number; satisfied: boolean[] },
): CampaignNextAction | null {
  const base = `/migrations/${encodeURIComponent(migrationId)}`;
  const meta = STEP_META[currentIndex];

  switch (meta.id) {
    case 'add-devices':
      return {
        stepId: meta.id,
        label: 'Add devices',
        to: `${base}/devices`,
        reason: 'No devices are assigned to this campaign yet.',
      };
    case 'map-identities':
      return {
        stepId: meta.id,
        label: 'Map identities',
        to: `${base}/identities`,
        reason:
          facts.notReady > 0
            ? `${plural(facts.notReady, 'device is', 'devices are')} waiting on an identity mapping.`
            : 'Reconcile each device’s user to the destination tenant.',
      };
    case 'activate-wave':
      // FD-493's dedicated preflight route (R-FD-051 D3), not the old inline
      // dialog: the checks are a page an operator reads before committing.
      return {
        stepId: meta.id,
        label: 'Activate wave',
        to: `${base}/activate`,
        reason: 'Review the preflight checks, then mint the wave credential.',
      };
    case 'build-package':
      // The package surface lives in the CONTEXT COLUMN on Overview (R-FD-051
      // D2 — there is no Package tab), so the CTA lands on the tab that holds it.
      return {
        stepId: meta.id,
        label: 'Build package',
        to: `${base}/overview`,
        reason: 'Devices are held at PACKAGE_NOT_READY until the package is built.',
      };
    case 'migrate':
      return {
        stepId: meta.id,
        label: 'Monitor devices',
        to: `${base}/devices`,
        reason: 'Devices fetch their flight plans on their own schedule.',
      };
    case 'complete':
      // Two very different situations share this index and only one of them has
      // an action. `satisfied[5]` true means the credential is already revoked —
      // the campaign is finished, and there is nothing left to press.
      if (facts.satisfied[LAST_INDEX]) return null;
      return {
        stepId: meta.id,
        label: 'Complete wave',
        to: `${base}/overview`,
        reason: 'Revoking the credential ends every device token minted from it.',
      };
    default:
      return null;
  }
}
